import { unstable_batchedUpdates } from 'react-dom';
import { Middleware } from 'redux';
import { omit } from 'underscore';
import isEqual from 'fast-deep-equal';

import { SET_LOADING_EVENT_BUNDLE, UPDATE_EVENT_BUNDLE } from '../actions/event/event-actions';
import { EventsState } from '../types';

// React 17 + react-redux 8 incompatibility: react-redux 8 uses the useSyncExternalStore shim,
// which calls forceUpdate inside useLayoutEffect for every useSelector subscriber. In React 17
// these updates are not batched, so a single dispatch can trigger 486+ synchronous forceUpdate
// calls in the SessionView subtree, exceeding React 17's nestedUpdateCount > 50 limit and
// throwing "Maximum update depth exceeded". React 18's auto-batching would fix this at the source.

// TODO: check whether this is needed after React 18 upgrade..

const bundleUpdateMiddleware: Middleware<{}, EventsState> = store => next => action => {
	if (action.type !== UPDATE_EVENT_BUNDLE) {
		return next(action);
	}

	const { eventBundle: prev } = store.getState().LiveEventReducer;
	const newBundle = action.payload;

	const IGNORED_FIELDS = ['created_date'] as const;

	// skip the dispatch entirely if nothing meaningful changed
	if (isEqual(omit(prev ?? {}, ...IGNORED_FIELDS), omit(newBundle, ...IGNORED_FIELDS))) {
		return;
	}

	// compare the number of enabled registration steps between the current and incoming bundle
	// an increase means new registration components (e.g. Stripe/ticketing) are about to mount
	const prevSteps = prev?.registration_steps?.filter((s: { isOn: boolean }) => s.isOn).length ?? 0;
	const nextSteps = newBundle?.registration_steps?.filter((s: { isOn: boolean }) => s.isOn).length ?? 0;
	const structureChanged = prev?.registration_on !== newBundle?.registration_on || prevSteps !== nextSteps;

	if (structureChanged) {
		// both dispatches are batched into one render so the component sees
		// loadingEventBundle=true and collapses to <Loader/>, unmounting all subscribers
		// before useLayoutEffect fires — preventing the forceUpdate cascade
		unstable_batchedUpdates(() => {
			next({ type: SET_LOADING_EVENT_BUNDLE, payload: null });
			next(action);
		});
	} else {
		next(action);
	}
};

export default bundleUpdateMiddleware;
